<template>
  <transactions-table
    :is-transactions-loading="isTransactionsLoading"
    :has-pagination="hasPagination"
    class="analytics-table"
    grid-template-columns="82px 1fr 1fr 1.1fr 1fr 1.2fr 1fr minmax(140px, 1.15fr) 60px"
  >
    <template #tableHeader>
      <transactions-table-header
        class="transactions-table__header"
        :header-cols="$options.HEADER_COLS"
        @sortChanged="handleSortChanged"
      ></transactions-table-header>
    </template>
    <template #transactionItems>
      <analytics-table-item
        :transaction="transaction"
        v-for="transaction in transactionsPaged"
        :key="transaction.operationId"
        @parentOperationIdClicked="handleParentOperationIdClicked"
      ></analytics-table-item>
    </template>
    <template #tablePagination>
      <transactions-table-pagination
        @changePage="handlePageChanged"
        :current-page="currentPage"
        :items-count="transactionsFiltered.length"
        :per-page="perPage"
      ></transactions-table-pagination>
    </template>
  </transactions-table>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
import TransactionsTable from '@vue-app/components/company/transactions/table/TransactionsTable.vue'
import TransactionsTableHeader from '@vue-app/components/company/transactions/table/TransactionsTableHeader.vue'
import TransactionTableItem from '@vue-app/components/company/transactions/table/TransactionsTableItem.vue'
import TransactionStatus from '@vue-app/components/transaction/TransactionStatus.vue'
import { Transaction } from '@js-app/entities/transaction/Transaction'
import AnalyticsTableItem from '@vue-app/components/company/analytics/table/item/AnalyticsTableItem.vue'
import moment from 'moment'
import TransactionsTablePagination from '@vue-app/components/company/transactions/table/TransactionsTablePagination.vue'

export default {
  name: 'CompanyAnalyticsTableV2',
  components: {
    TransactionsTablePagination,
    AnalyticsTableItem,
    TransactionStatus,
    TransactionTableItem,
    TransactionsTableHeader,
    TransactionsTable,
  },
  props: {
    transactionsList: {
      type: Array,
      default() {
        return []
      },
    },
    transactionType: {
      type: Object,
      default: null,
    },
    hasPagination: {
      type: Boolean,
      default: true,
    },
    isTransactionsLoading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    currentPage: 1,
    perPage: 10,
    sort: {
      col: null,
      dir: null,
    },
  }),
  HEADER_COLS: [
    {
      id: 'date',
      hasSort: true,
      sortKey: 'date',
      title: 'analytics.table.header.date',
      additionalClasses: ['transactions-table-header__col--date'],
    },
    {
      id: 'transactionType',
      hasSort: true,
      sortKey: 'transactionType',
      title: 'analytics.table.header.transaction-type',
      additionalClasses: ['transactions-table-header__col--transaction-type'],
    },
    {
      id: 'partnerTransactionId',
      hasSort: true,
      sortKey: 'partnerTransactionId',
      title: 'analytics.table.header.partner-transaction-id',
      additionalClasses: ['transactions-table-header__col--pt-id'],
    },
    {
      id: 'operationId',
      hasSort: true,
      sortKey: 'operationId',
      title: 'analytics.table.header.operation-id',
      additionalClasses: ['transactions-table-header__col--op-id'],
    },
    {
      id: 'orderNumber',
      hasSort: true,
      sortKey: 'orderNumber',
      title: 'analytics.table.header.order-number',
      additionalClasses: ['transactions-table-header__col--order-number'],
    },
    {
      id: 'senderRecipient',
      hasSort: true,
      sortKey: 'senderRecipient',
      title: 'analytics.table.header.sender-recipient',
      additionalClasses: ['transactions-table-header__col--sender-recipient'],
    },
    // {
    //   id: "mid",
    //   hasSort: true,
    //   sortKey: "mid",
    //   title: "analytics.table.header.mid",
    //   additionalClasses: ["transactions-table-header__col--mid"],
    // },
    {
      id: 'amount',
      hasSort: true,
      sortKey: 'amount',
      title: 'analytics.table.header.amount',
      additionalClasses: ['transactions-table-header__col--amount'],
    },
    {
      id: 'status',
      hasSort: true,
      sortKey: 'status',
      title: 'analytics.table.header.status',
      additionalClasses: ['transactions-table-header__col--status'],
    },
    {
      id: 'action',
      hasSort: false,
      title: 'analytics.table.header.action',
      additionalClasses: ['transactions-table-header__col--action'],
    },
  ],
  computed: {
    transactionsPaged() {
      let _startFrom = (this.currentPage - 1) * this.perPage
      return this.transactionsFiltered.slice(
        _startFrom,
        _startFrom + this.perPage
      )
    },
    transactionsFiltered() {
      let _transactionToProcess = [...this.transactionsSorted]

      if (this.transactionType) {
        _transactionToProcess = _transactionToProcess.filter(
          (t) =>
            this.transactionType.id === t.transactionType ||
            this.transactionType.id === t.operationTypeId
        )
      }

      this.currentPage = 1

      return _transactionToProcess
    },
    transactionsSorted() {
      if (!this.sort || !this.sort.col) {
        return this.transactions
      }

      return [...this.transactions].sort((fTransaction, sTransaction) => {
        let _firstGrater = false

        switch (this.sort.col) {
          case 'date': {
            let _firstDate = moment(fTransaction.operationDate)
            let _secondDate = moment(sTransaction.operationDate)

            if (_firstDate > _secondDate) {
              _firstGrater = true
            }
            break
          }
          case 'section': {
            if (fTransaction.operationTypeId > sTransaction.operationTypeId) {
              _firstGrater = true
            }

            break
          }
          case 'operationID': {
            if (fTransaction.operationId > sTransaction.operationId) {
              _firstGrater = true
            }

            break
          }
          case 'client': {
            if (fTransaction.userFullName > sTransaction.userFullName) {
              _firstGrater = true
            }

            break
          }
          case 'transactionType': {
            if (fTransaction.transactionType > sTransaction.transactionType) {
              _firstGrater = true
            }

            break
          }
          case 'partnerTransactionId': {
            if (
              fTransaction.partnerTransactionId >
              sTransaction.partnerTransactionId
            ) {
              _firstGrater = true
            }

            break
          }
          case 'orderNumber': {
            if (fTransaction.orderNumber > sTransaction.orderNumber) {
              _firstGrater = true
            }

            break
          }
          case 'amount': {
            if (
              parseInt(fTransaction.transactionAmountPlain) >
              parseInt(sTransaction.transactionAmountPlain)
            ) {
              _firstGrater = true
            }

            break
          }
          case 'currency': {
            if (
              parseInt(fTransaction.transactionCurrencyPlain) >
              parseInt(sTransaction.transactionCurrencyPlain)
            ) {
              _firstGrater = true
            }

            break
          }
          case 'status': {
            if (
              parseInt(fTransaction.operationStatus) >
              parseInt(sTransaction.operationStatus)
            ) {
              _firstGrater = true
            }

            break
          }
        }

        switch (this.sort.dir) {
          case 'DESC': {
            if (_firstGrater) {
              return -1
            } else {
              return 1
            }
          }
          case 'ASC': {
            if (_firstGrater) {
              return 1
            } else {
              return -1
            }
          }
        }
      })
    },
    transactions() {
      return this.transactionsList.map(
        (_transObject) => new Transaction(_transObject)
      )
    },
  },
  watch: {
    transactionType() {
      this.currentPage = 1
    },
  },
  methods: {
    handlePageChanged(page) {
      this.currentPage = page
    },
    handleSortChanged(newSort) {
      this.sort = { ...newSort }
    },
    handleParentOperationIdClicked(operationId) {
      $emit(this, 'parentOperationIdClicked', operationId)
    },
  },
  emits: ['parentOperationIdClicked'],
}
</script>

<style lang="scss" scoped>
.analytics-table {
  :deep(.transactions-table) {
    .transactions-table__header {
      padding: 30px 10px;
      background: #ffffff;
      border-radius: 20px 20px 0 0;
      border-bottom: 1px solid #eff2f8;
    }

    .transactions-table__body {
      background: #ffffff;
      border-radius: 0 0 20px 20px;
      padding-bottom: 30px;
    }
  }

  :deep(.transactions-table-header) {
    .transactions-table-header__col {
      .transactions-table-header__col--amount--amount {
        justify-content: flex-end;
      }

      .transactions-table-header__col--amount--status {
        justify-content: center;
      }

      .transactions-table-header__col--amount--action {
        display: flex;
        justify-content: center;
      }
    }
  }
}
</style>
